import {
  EarningsInsight,
  EarningsInsightFilterResponse,
  EarningsInsightFilterV4Response,
  EarningsInsightsFilterRequest,
  EarningsInsightType,
  earningsInsights,
} from '@api/api/earnings-insights';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQuery } from '@tanstack/react-query';
import { useKgInsightFilter } from '@terminal/react-query-hooks/insights/useKgInsightFilter';
import {
  mapEarningsInsightsFilterRequestToKgFilterRequest,
  mapKgInsightToEarningsInsight,
} from '@terminal/services/insights/utils/catalyst-insight-to-kg-mapper';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { useMemo } from 'react';

type ThemeEarningsInsightListItem =
  | EarningsInsight
  | EarningsInsightFilterV4Response[number];
type ThemeEarningsInsightFilterResponse =
  | EarningsInsightFilterResponse
  | EarningsInsightFilterV4Response;

export type ThemeEarningsInsight = ThemeEarningsInsightListItem & {
  source: 'earnings-insight' | 'kg-insight';
  /** The earnings-insight this KG insight beat; only used if its detail fetch fails. */
  fallbackEarningsInsightId?: string;
};

export type ThemeEarningsInsightsForEarnings = Partial<
  Record<EarningsInsightType, ThemeEarningsInsight>
>;

const QUERY_KEY_THEME_EARNINGS_INSIGHTS_FILTER =
  'QUERY_KEY_THEME_EARNINGS_INSIGHTS_FILTER';

const MAX_PAGE_SIZE = 100;

const fetchThemeEarningsInsights = ({
  filterBody,
  isV4Enabled,
  signal,
}: {
  filterBody: EarningsInsightsFilterRequest;
  isV4Enabled: boolean;
  signal?: AbortSignal;
}) => {
  if (isV4Enabled) {
    const { path, schema } = earningsInsights.postEarningsInsightFilterV4;
    return wretchRequest<EarningsInsightFilterV4Response>(path, {
      method: 'post',
      body: filterBody,
      schema,
      signal,
    });
  }

  const { path, schema } = earningsInsights.postEarningsInsightFilter;
  return wretchRequest<EarningsInsightFilterResponse>(path, {
    method: 'post',
    body: filterBody,
    schema,
    signal,
  });
};

/**
 * Prefer kg over earnings-insight for the same type; keep preview and recap.
 * A losing earnings-insight is kept as `fallbackEarningsInsightId` so callers
 * can recover when the winning KG insight's detail fetch fails.
 */
const groupThemeEarningsInsights = (
  insights: ThemeEarningsInsight[]
): Map<string, ThemeEarningsInsightsForEarnings> => {
  const byEarningsId = new Map<string, ThemeEarningsInsightsForEarnings>();

  for (const insight of insights) {
    const current = byEarningsId.get(insight.earnings_id) ?? {};
    const chosen = current[insight.type];

    if (!chosen) {
      current[insight.type] = insight;
      byEarningsId.set(insight.earnings_id, current);
      continue;
    }

    const isFallbackForChosen =
      chosen.source === 'kg-insight' &&
      insight.source === 'earnings-insight' &&
      !chosen.fallbackEarningsInsightId;

    if (isFallbackForChosen) {
      current[insight.type] = {
        ...chosen,
        fallbackEarningsInsightId: insight.id,
      };
    }
  }

  return byEarningsId;
};

interface UseThemeEarningsInsightsProps {
  earningsIds?: string[];
  entityTags?: string[];
  insightTypes?: EarningsInsightType[];
  isEnabled?: boolean;
  pageSize?: number;
}

/**
 * ThemeEarnings-only listing: merges earnings-insights + kg-insights
 * (earnings_recap / earnings_preview only — not company/market catalysts).
 */
export const useThemeEarningsInsights = ({
  earningsIds = [],
  entityTags = [],
  insightTypes,
  isEnabled = true,
  pageSize = MAX_PAGE_SIZE,
}: UseThemeEarningsInsightsProps) => {
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const isEarningsInsightsV4Enabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2467-earnings-insights-v4')
  );

  const shouldFetch =
    isEnabled &&
    !isBasicUser &&
    (earningsIds.length > 0 || entityTags.length > 0);
  const filterBody: EarningsInsightsFilterRequest = {
    ...(earningsIds.length > 0 && { earnings_ids: earningsIds }),
    ...(entityTags.length > 0 && { entities: entityTags }),
    ...(insightTypes?.length && { types: insightTypes }),
    page: 1,
    page_size: pageSize,
  };

  const { data: earningsData = [], isFetching: isEarningsFetching } =
    useQuery<ThemeEarningsInsightFilterResponse>({
      queryKey: [
        QUERY_KEY_THEME_EARNINGS_INSIGHTS_FILTER,
        isEarningsInsightsV4Enabled ? 'v4' : 'v1',
        filterBody,
      ],
      queryFn: ({ signal }) =>
        fetchThemeEarningsInsights({
          filterBody,
          isV4Enabled: isEarningsInsightsV4Enabled,
          signal,
        }),
      staleTime: getTimeTillEndOfDayMs(),
      enabled: shouldFetch,
    });

  const {
    data: kgData,
    isFetching: isKgFetching,
    isLoading: isKgFilterLoading,
  } = useKgInsightFilter(
    mapEarningsInsightsFilterRequestToKgFilterRequest(filterBody),
    shouldFetch
  );

  const earningsInsightByEarningsId = useMemo(() => {
    const kgInsights: ThemeEarningsInsight[] = shouldFetch
      ? (kgData ?? [])
          .filter(insight => !!insight.subject.ref?.id)
          .map(insight => ({
            ...mapKgInsightToEarningsInsight(insight),
            source: 'kg-insight' as const,
          }))
      : [];

    const earningsInsightsWithSource: ThemeEarningsInsight[] = earningsData.map(
      insight => ({
        ...insight,
        source: 'earnings-insight' as const,
      })
    );

    return groupThemeEarningsInsights([
      ...kgInsights,
      ...earningsInsightsWithSource,
    ]);
  }, [earningsData, kgData, shouldFetch]);

  return {
    earningsInsightByEarningsId,
    isKgFilterLoading,
    isEarningsInsightsFetching:
      isEarningsFetching || (shouldFetch && isKgFetching),
  };
};
